import React, { useEffect } from "react";
import { useI18n } from "../../utils/i18n/I18nProvider";
import { useServices } from "../../services/ServicesProvider";
import { initializePerformance } from "../../utils/performance";

interface AppWithLoadingStateProps {
  children: React.ReactNode;
}

/**
 * This wrapper component ensures the loading screen stays visible
 * until both the i18n resources and UserService are fully loaded, preventing the flash
 * of untranslated text (FOUT) and ensuring user data is available before rendering.
 * It also initializes the performance detection system early in the app lifecycle.
 */
export function AppWithLoadingState({ children }: AppWithLoadingStateProps) {
  const { isLoaded: isI18nLoaded } = useI18n();
  const { userService } = useServices();

  useEffect(() => {
    // Initialize performance detection early in app lifecycle
    initializePerformance().catch((error) => {
      console.error("Failed to initialize performance detection:", error);
    });
  }, []);

  useEffect(() => {
    if (isI18nLoaded && userService) {
      console.log(
        "✅ i18n resources and UserService loaded, hiding loading screen"
      );

      // Hide loading screen after React has mounted and everything is ready
      // Exception: Don't hide for game page, let GameComponent handle it
      setTimeout(() => {
        const currentPath = window.location.pathname;
        if (
          currentPath !== "/game" &&
          typeof window.hideLoadingScreen === "function"
        ) {
          window.hideLoadingScreen();
        }
      }, 100);
    }
  }, [isI18nLoaded, userService]);

  // Don't render children until both i18n and UserService are loaded
  if (!isI18nLoaded || !userService) {
    return null;
  }

  return <>{children}</>;
}
